import { IFeed } from 'models/IFeed'
import { IGroupApexChart } from 'models/IGroup'
import moment, { Moment } from 'moment'
import { useEffect, useMemo, useState } from 'react'
import GroupsApiService from 'services/groupsApiService'
import PlanApiService from 'services/planApiService'
import { aggregateAllGroupsToFetchData } from 'components/farm-dashboard/components/widgets/feed-consumption/hook/aggregateFeedConsumptionAllGroups'
import { buildCleaningCards } from './cleaningMetrics'
import { MetricCardData } from './dayMetrics'
import {
	buildDistributionCards,
	emptyMinutesFromGraphs,
	TrendPoint,
} from './distributionMetrics'
import {
	buildFeedNudgeCards,
	buildPsvCard,
	FeedNudgeMetrics,
	GroupGraph,
	PsvPoint,
	PushPoint,
} from './nudgeMetrics'

type MetricGroup = {
	id: number
	title: string
	dryNorm: number | null
}

type UseCleaningMetricsArgs = {
	startDate: Moment
	endDate: Moment
	selectedGroupIds: number[]
	enabledGroupIds: number[]
	groups: MetricGroup[]
}

const datesBetween = (startDate: Moment, endDate: Moment) => {
	const days: string[] = []
	const cursor = startDate.clone().startOf('day')
	const last = endDate.clone().startOf('day')
	const [from, to] = cursor.isAfter(last) ? [last, cursor] : [cursor, last]

	while (from.isSameOrBefore(to, 'day') && days.length < 62) {
		days.push(from.format('YYYY-MM-DD'))
		from.add(1, 'day')
	}

	return days
}

export const useFeedingPlanMetrics = ({
	startDate,
	endDate,
	selectedGroupIds,
	enabledGroupIds,
	groups,
}: UseCleaningMetricsArgs): {
	cleaningCards: MetricCardData[]
	distributionCards: MetricCardData[]
	pushesCard: MetricCardData
	nudgeCards: MetricCardData[]
	psvCard: MetricCardData
	pushTrend: PushPoint[]
	psvTrend: PsvPoint[]
	trend: TrendPoint[]
	showTrend: boolean
	hasEnabledData: boolean
	plansReady: boolean
} => {
	const [plansReady, setPlansReady] = useState(false)
	const [plansByDay, setPlansByDay] = useState<
		{ day: string; plans: IFeed[] }[]
	>([])
	const [graphs, setGraphs] = useState<GroupGraph[]>([])
	const [psvFact, setPsvFact] = useState<number | null>(null)
	const [pushTrend, setPushTrend] = useState<PushPoint[]>([])
	const [psvTrend, setPsvTrend] = useState<PsvPoint[]>([])
	const [nudgeMetrics, setNudgeMetrics] = useState<FeedNudgeMetrics | null>(
		null,
	)
	const rangeKey = `${startDate.format('YYYY-MM-DD')}:${endDate.format('YYYY-MM-DD')}`
	const groupsKey = enabledGroupIds.join(',')

	useEffect(() => {
		let cancelled = false
		setPlansReady(false)
		const days = datesBetween(startDate, endDate)

		Promise.all(
			days.map(async day => {
				try {
					const response = await PlanApiService.getFeedplans(day)
					const plans: IFeed[] = Array.isArray(response.data)
						? response.data
						: []
					return { day, plans }
				} catch {
					return { day, plans: [] as IFeed[] }
				}
			}),
		).then(result => {
			if (!cancelled) {
				setPlansByDay(result)
				setPlansReady(true)
			}
		})

		return () => {
			cancelled = true
		}
	}, [endDate, rangeKey, startDate])

	useEffect(() => {
		let cancelled = false
		const ids =
			selectedGroupIds.length > 0
				? selectedGroupIds.filter(id => enabledGroupIds.includes(id))
				: enabledGroupIds
		if (ids.length === 0) {
			setGraphs([])
			return () => {
				cancelled = true
			}
		}
		const start = startDate.clone().startOf('day').valueOf()
		const end = endDate.clone().endOf('day').valueOf()

		Promise.all(
			ids.map(async id => {
				try {
					const response =
						await GroupsApiService.getFoodTableReachableAndUnreachable(
							id,
							start,
							end,
						)
					return {
						groupId: id,
						points: Array.isArray(response.data)
							? (response.data as IGroupApexChart[])
							: [],
					}
				} catch {
					return { groupId: id, points: [] as IGroupApexChart[] }
				}
			}),
		).then(result => {
			if (!cancelled) setGraphs(result)
		})

		return () => {
			cancelled = true
		}
	}, [enabledGroupIds, endDate, groupsKey, selectedGroupIds, startDate])

	useEffect(() => {
		let cancelled = false
		const ids =
			selectedGroupIds.length > 0
				? selectedGroupIds.filter(id => enabledGroupIds.includes(id))
				: enabledGroupIds
		const start = startDate.clone().startOf('day').valueOf()
		const end = endDate.clone().endOf('day').valueOf()

		const load = async () => {
			const list = await Promise.all(
				ids.map(async id => {
					try {
						const response = await GroupsApiService.getDataForConsuptionChart(
							id,
							start,
							end,
						)
						const item = Array.isArray(response.data)
							? response.data[0]
							: response.data
						return {
							title: item?.title ?? '',
							dots: Array.isArray(item?.dots) ? item.dots : [],
						}
					} catch {
						return { title: '', dots: [] }
					}
				}),
			)
			return aggregateAllGroupsToFetchData(list, start, end).fetchData
		}

		load()
			.then(data => {
				if (cancelled) return
				const valued = data.filter(
					point => point.distributed_sum != null || point.cleaned_sum != null,
				)
				setPsvFact(
					valued.length
						? valued.reduce((sum, point) => sum + (point.psv || 0), 0) /
								valued.length
						: null,
				)
			})
			.catch(() => {
				if (!cancelled) setPsvFact(null)
			})

		return () => {
			cancelled = true
		}
	}, [enabledGroupIds, endDate, groupsKey, selectedGroupIds, startDate])

	useEffect(() => {
		let cancelled = false
		const ids =
			selectedGroupIds.length > 0
				? selectedGroupIds.filter(id => enabledGroupIds.includes(id))
				: enabledGroupIds
		const from = startDate.clone().startOf('day')
		const today = moment().startOf('day')
		let to = endDate.clone().startOf('day')
		if (!to.isBefore(today)) to = today.clone().subtract(1, 'day')

		if (ids.length === 0 || from.isAfter(to)) {
			setPushTrend([])
			setPsvTrend([])
			setNudgeMetrics(null)
			return () => {
				cancelled = true
			}
		}

		setNudgeMetrics(null)

		GroupsApiService.getFeedAnalytics(
			from.format('YYYY-MM-DD'),
			to.format('YYYY-MM-DD'),
			ids,
		)
			.then(response => {
				if (cancelled) return
				const always = response.data?.always_visible_metrics
				const metrics = response.data?.conditional_metrics
				setNudgeMetrics({
					scheduledValue: always?.scheduled_pushes?.value ?? null,
					scheduledQuality: always?.scheduled_pushes?.quality ?? null,
					decline: always?.avg_decline_feed ?? null,
					pushesAm: always?.avg_pushes_am ?? null,
					pushesNight: always?.avg_pushes_night ?? null,
					minPushes: metrics?.min_pushes ?? null,
					maxPushes: metrics?.max_pushes ?? null,
					avgPushes: metrics?.avg_pushes ?? null,
				})
				const pushes = new Map<string, number | null>(
					(metrics?.pushes_trend ?? []).map(
						(point: {
							date: string
							pushes: number | null
						}): [string, number | null] => [point.date, point.pushes],
					),
				)
				const dry = new Map<
					string,
					{ avg_dry: number | null; heads: number | null }
				>(
					(metrics?.avg_dry_trend ?? []).map(
						(point: {
							date: string
							avg_dry: number | null
							heads: number | null
						}): [string, { avg_dry: number | null; heads: number | null }] => [
							point.date,
							point,
						],
					),
				)
				const days = datesBetween(startDate, endDate)
				setPushTrend(
					days.map(day => ({
						date: day,
						count: pushes.has(day) ? (pushes.get(day) ?? null) : null,
					})),
				)
				setPsvTrend(
					days.map(day => {
						const point = dry.get(day)
						return {
							date: moment(day).startOf('day').valueOf(),
							psv: point ? point.avg_dry : null,
							heads: point?.heads ?? 0,
						}
					}),
				)
			})
			.catch(() => {
				if (!cancelled) {
					setPushTrend([])
					setPsvTrend([])
					setNudgeMetrics(null)
				}
			})

		return () => {
			cancelled = true
		}
	}, [enabledGroupIds, endDate, groupsKey, selectedGroupIds, startDate])

	return useMemo(() => {
		const allowed = new Set(enabledGroupIds)
		const scopedIds =
			selectedGroupIds.length > 0
				? selectedGroupIds.filter(id => allowed.has(id))
				: enabledGroupIds
		const period = !startDate.isSame(endDate, 'day')
		const multipleGroups =
			selectedGroupIds.length > 1 ||
			(selectedGroupIds.length === 0 && scopedIds.length > 1)

		const scope = {
			groupIds: scopedIds,
			multipleGroups,
			period,
		}
		const distribution = buildDistributionCards(
			plansByDay,
			scope,
			emptyMinutesFromGraphs(graphs.map(graph => graph.points)),
		)
		const nudges = buildFeedNudgeCards(nudgeMetrics, scope, groups)
		const enabled = new Set(enabledGroupIds)
		const hasEnabledData = plansByDay.some(day =>
			day.plans.some(plan =>
				plan.feed_facts?.some(fact => {
					const groupId = fact.group?.id
					return groupId != null && enabled.has(groupId)
				}),
			),
		)
		const norms = groups.filter(
			group => scopedIds.includes(group.id) && group.dryNorm != null,
		)
		const norm = norms.length
			? norms.reduce((sum, group) => sum + (group.dryNorm ?? 0), 0) /
				norms.length
			: null

		return {
			cleaningCards: buildCleaningCards(plansByDay, scope),
			distributionCards: distribution.cards,
			pushesCard: nudges.pushesCard,
			nudgeCards: nudges.cards,
			psvCard: buildPsvCard(psvFact, norm),
			pushTrend,
			psvTrend,
			trend: distribution.trend,
			showTrend: period,
			hasEnabledData,
			plansReady,
		}
	}, [
		enabledGroupIds,
		endDate,
		graphs,
		groups,
		nudgeMetrics,
		plansByDay,
		plansReady,
		psvFact,
		psvTrend,
		pushTrend,
		selectedGroupIds,
		startDate,
	])
}
